import { ChangeEvent, useMemo, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";

import { authSessionAtom, authTokenAtom } from "atoms/authAtoms";
import { errorAtom } from "atoms/error";
import { useSnackbar } from 'utils/hooks/useSnackbar';
import { submitAllCopyExcel, submitSubCopyExcel, submitAllCopyExcelHev, submitSubCopyExcelHev, type CopyFailedItem } from "api/submitRtool";
import { copyToConfigAtom } from "atoms/Rtool/copyToConfigAtoms";
import { mappingAtom } from "atoms/mappingAtoms";
import { powertrainAtom } from "atoms/powertrainAtom";
import { gearIdAtom } from "atoms/gearAtom";
import { isConfigComplete } from "utils/utils";
import { buildLabeledOptions } from "utils/mappingLabel";
import { naturalSort } from "utils/sort";
import { DATASET_IDS, DRIVE_MODE_IDS, PHENOMENON_IDS } from "utils/consts";
import { DRIVE_MODE_IDS as HEV_DRIVE_MODE_IDS, PHENOMENON_IDS as HEV_PHENOMENON_IDS } from "utils/constsHev";
import { uploadExcelToS3 } from "../utils/uploadExcelToS3";
import { uploadDatasetExcelToS3 } from "../utils/uploadDatasetExcelToS3";
import { toUploadErrorMessage, toUserMessage } from "utils/apiErrorMessage";
import { FailedItemsList, buildCopyErrorMessage, classifySummary, buildResultMessage } from "components/FailedItemsList";
import { useIsCopyJobBlocking } from "utils/hooks/useCopyJobBlocking";

export const ExcelImport: React.FC = () => {
    const authToken = useAtomValue(authTokenAtom);
    const authSession = useAtomValue(authSessionAtom);
    const config = useAtomValue(copyToConfigAtom);
    const mapping = useAtomValue(mappingAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const gearId = useAtomValue(gearIdAtom);
    const setError = useSetAtom(errorAtom);
    const { showSuccess, showError, showWarning } = useSnackbar();
    const isCopyBlocking = useIsCopyJobBlocking(powertrain, config);

    // HEV は aco を含まない HEV 固有 condition 値域＋gear 単位 URL/キー（HEV対応仕様 §3, S3設計 §3.2）
    const isHev = powertrain === 'hev';
    const driveModeIds = isHev ? HEV_DRIVE_MODE_IDS : DRIVE_MODE_IDS;
    const phenomenonIds = isHev ? HEV_PHENOMENON_IDS : PHENOMENON_IDS;
    const gearArg = isHev ? (gearId ?? undefined) : undefined;

    // 現象・運転モードは定義順で表示する（各条件表示・マトリクスの軸順と一致させる）
    const driveModeOptions = useMemo(
        () => buildLabeledOptions(mapping, 'drive_mode_id', driveModeIds),
        [mapping, driveModeIds],
    );
    const phenomenonOptions = useMemo(
        () => buildLabeledOptions(mapping, 'phenomenon_id', phenomenonIds),
        [mapping, phenomenonIds],
    );
    const datasetIds = useMemo(() => naturalSort(DATASET_IDS), []);

    const [loading, setLoading] = useState(false);
    const [copyOpen, setCopyOpen] = useState(false);
    const [copyMode, setCopyMode] = useState<"all" | "sub">("all");

    const [drive_mode_id, setDriveModeId] = useState<string>("");
    const [phenomenon_id, setPhenomenonId] = useState<string>("");
    const [dataset_id, setDatasetId] = useState<string>("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [failedItems, setFailedItems] = useState<CopyFailedItem[]>([]);
    const [copyMessage, setCopyMessage] = useState<string | null>(null);

    const resetSelections = () => {
        setDriveModeId("");
        setPhenomenonId("");
        setDatasetId("");
        setSelectedFile(null);
        setFailedItems([]);
        setCopyMessage(null);
    };

    const openCopyDialog = (mode: "all" | "sub") => {
        setCopyMode(mode);
        resetSelections();
        setCopyOpen(true);
    };

    const handleDialogClose = () => {
        setCopyOpen(false);
        resetSelections();
    };

    const extractFileFromEvent = (event: ChangeEvent<HTMLInputElement>): File | null => {
        const input = event.target;
        const file = input.files?.[0] ?? null;
        input.value = "";
        return file;
    };

    type FileSelectorProps = {
        file: File | null;
        label: string;
        onChange: (file: File | null) => void;
    };

    const FileSelector = ({ file, label, onChange }: FileSelectorProps) => (
        <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
            <Button variant="outlined" component="label">
                {label}
                <input
                    hidden
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        onChange(extractFileFromEvent(event))
                    }
                />
            </Button>
            {file && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    選択中: {file.name}
                </Typography>
            )}
        </Stack>
    );

    const handleConfirm = async () => {
        setError(null);
        setFailedItems([]);
        setCopyMessage(null);

        if (!selectedFile || !drive_mode_id || !phenomenon_id) {
            showWarning("ドライブモードID、現象ID、ファイルを指定してください。");
            return;
        }

        setLoading(true);
        try {
            if (copyMode === "all") {
                try {
                    await uploadExcelToS3(
                        authSession,
                        selectedFile,
                        config,
                        drive_mode_id,
                        phenomenon_id,
                        powertrain,
                        gearArg,
                    );
                } catch (e) {
                    const message = toUploadErrorMessage(e);
                    setError(message);
                    showError(message);
                    return;
                }

                const response = isHev
                    ? await submitAllCopyExcelHev(authToken, config, gearId!, drive_mode_id, phenomenon_id)
                    : await submitAllCopyExcel(authToken, config, drive_mode_id, phenomenon_id);

                if (response.status >= 400) {
                    setCopyMessage(buildCopyErrorMessage(response));
                    setFailedItems(response.failed_items ?? []);
                    return;
                }
                const failed = response.failed_items ?? [];
                // null_points warning は該当データが登録成功したまま付く注意情報のため、
                // 全体の成否（all_success/partial_failure 等）に関わらず件数を注記する。
                const nullPointsCount = (response.warnings ?? []).filter((w) => w.code === 'null_points').length;
                const warningNote = nullPointsCount > 0
                    ? `（一部データで補間点が欠けています: ${nullPointsCount}件）`
                    : '';
                if (!response.summary) {
                    if (failed.length > 0) {
                        setCopyMessage("一部の登録に失敗しました。");
                        setFailedItems(failed);
                        return;
                    }
                    showSuccess((response.message || "コピー処理が完了しました。") + warningNote);
                } else if (classifySummary(response.summary) === 'all_success') {
                    showSuccess(buildResultMessage(response.summary) + warningNote);
                } else {
                    setCopyMessage(buildResultMessage(response.summary) + warningNote);
                    setFailedItems(failed);
                    return;
                }
            } else {
                if (!dataset_id) {
                    showWarning("データセットIDを選択してください。");
                    setLoading(false);
                    return;
                }

                try {
                    await uploadDatasetExcelToS3(
                        authSession,
                        selectedFile,
                        config,
                        drive_mode_id,
                        phenomenon_id,
                        dataset_id,
                        powertrain,
                        gearArg,
                    );
                } catch (e) {
                    const message = toUploadErrorMessage(e);
                    setError(message);
                    showError(message);
                    return;
                }

                const response = isHev
                    ? await submitSubCopyExcelHev(authToken, config, gearId!, drive_mode_id, phenomenon_id, dataset_id)
                    : await submitSubCopyExcel(authToken, config, drive_mode_id, phenomenon_id, dataset_id);

                if (response.status >= 400) {
                    setCopyMessage(buildCopyErrorMessage(response));
                    return;
                }
                showSuccess(response.message || "コピー処理が完了しました。");
            }
            handleDialogClose();
        } catch (e) {
            const message = toUserMessage(e);
            setError(message);
            showError(message);
        } finally {
            setLoading(false);
        }
    };

    const confirmDisabled =
        loading ||
        !selectedFile ||
        !drive_mode_id ||
        !phenomenon_id ||
        (copyMode === "sub" && !dataset_id) ||
        (isHev && !gearId) ||
        isCopyBlocking;

    return (
        <Box p={2}>
            <Typography>Excelファイルを読み込みます。</Typography>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button
                    variant="contained"
                    sx={{ minWidth: 140 }}
                    disabled={!isConfigComplete(config) || loading || isCopyBlocking}
                    onClick={() => openCopyDialog("all")}
                >
                    {loading ? "コピー中..." : "全データコピー"}
                </Button>
                <Button
                    variant="contained"
                    sx={{ minWidth: 140 }}
                    disabled={!isConfigComplete(config) || loading || isCopyBlocking}
                    onClick={() => openCopyDialog("sub")}
                >
                    {loading ? "コピー中..." : "個別データコピー"}
                </Button>
            </Stack>

            {isCopyBlocking && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                    コピー実行中のため操作できません。
                </Typography>
            )}

            <Dialog open={copyOpen} onClose={handleDialogClose} fullWidth maxWidth="xs">
                <DialogTitle>
                    {copyMode === "all"
                        ? "ドライブモードIDと現象IDとExcelファイルを選択してください。"
                        : "ドライブモードIDと現象IDとデータセットIDとExcelファイルを選択してください"}
                </DialogTitle>

                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel id="drive-select-label">ドライブモードID</InputLabel>
                        <Select
                            labelId="drive-select-label"
                            label="ドライブID"
                            value={drive_mode_id}
                            onChange={(e) => setDriveModeId(e.target.value as string)}
                        >
                            {driveModeOptions.map(({ value, label }) => (
                                <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel id="phenomenon-select-label">現象ID</InputLabel>
                        <Select
                            labelId="phenomenon-select-label"
                            label="現象ID"
                            value={phenomenon_id}
                            onChange={(e) => setPhenomenonId(e.target.value as string)}
                        >
                            {phenomenonOptions.map(({ value, label }) => (
                                <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {copyMode === "sub" && (
                        <FormControl fullWidth sx={{ mt: 1 }}>
                            <InputLabel id="dataset-select-label">データセットID</InputLabel>
                            <Select
                                labelId="dataset-select-label"
                                label="データセットID"
                                value={dataset_id}
                                onChange={(e) => setDatasetId(e.target.value as string)}
                            >
                                {datasetIds.map((id) => (
                                    <MenuItem key={id} value={id}>{id}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <FileSelector
                        file={selectedFile}
                        label="Excelファイルを選択"
                        onChange={setSelectedFile}
                    />
                    {copyMessage && (
                        <Box sx={{ mt: 2 }}>
                            <Typography color="error" variant="body2">
                                {copyMessage}
                            </Typography>
                            <FailedItemsList items={failedItems} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>キャンセル</Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={confirmDisabled}
                        startIcon={loading ? <CircularProgress size={18} /> : undefined}
                    >
                        {loading ? "コピー中..." : "確定"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
